import type { FC } from "react";
import { ImageWithFallback } from "@/components/image/ImageWithFallback";
import { isNonNullable } from "@/types/isNonNullable";
import { EntityLogo } from "@/components/entity-logo/EntityLogo";
import classNames from "classnames";
import styles from "./TransferCard.module.scss";
import type { TransferCardComponent } from "@motain/xpa-proto-files-web/lib/types/transfers";
import { transferStatePipe } from "./transferStatePipe";
import { TransferLikelihood } from "@motain/xpa-proto-files-web/lib/types/transfers";
import Link from "next/link";

const FALLBACK_PLAYER_IMG = {
  dark: "https://images.onefootball.com/cw/icons/empty_player_dark.svg",
  light: "https://images.onefootball.com/cw/icons/empty_player_light.svg",
};

type TransferCardProps = { className?: string } & TransferCardComponent;

export const TransferCard: FC<TransferCardProps> = (props) => {
  const {
    title,
    transferTimeTimestamp,
    transferTimeFormatted,
    playerPosition,
    playerName,
    playerImg,
    teamImgNew,
    teamNameOld,
    teamNameNew,
    state,
    className,
    urlPath,
    trackingEvents,
  } = props;

  const transferState = transferStatePipe(state);
  const isLink = isNonNullable(urlPath);

  const WrapperTag = isLink && urlPath !== "" ? Link : "article";

  const isSendTrackingEvent =
    isLink && urlPath !== "" && isNonNullable(trackingEvents);

  return (
    <WrapperTag
      className={classNames(
        styles.container,
        className,
        isNonNullable(urlPath) && styles.containerLink,
      )}
      href={urlPath}
      onClick={() => isSendTrackingEvent && (() => trackingEvents)}
    >
      <header className={classNames("title-8-bold", styles.title)}>
        {title}{" "}
        <time
          dateTime={transferTimeTimestamp}
          className={classNames("title-8-regular", styles.titleTime)}
        >
          {transferTimeFormatted}
        </time>
      </header>

      <div className={styles.content}>
        {isNonNullable(playerImg) && (
          <ImageWithFallback
            width={32}
            height={32}
            src={playerImg.path}
            alt={playerImg.alt}
            isPreloaded={playerImg.isPreloaded}
            fallbackImageSrc={FALLBACK_PLAYER_IMG.light}
            fallbackImageSrcDark={FALLBACK_PLAYER_IMG.dark}
            className={styles.contentPlayerImage}
          ></ImageWithFallback>
        )}

        <div className={styles.contentPlayerInfo}>
          <p
            className={classNames("title-7-bold", styles.contentPlayerInfoName)}
          >
            {playerName}
          </p>

          <p
            className={classNames(
              "title-8-regular",
              styles.contentPlayerInfoPosition,
            )}
          >
            {playerPosition}
          </p>
        </div>

        {isNonNullable(teamImgNew) && (
          <EntityLogo
            src={teamImgNew.path}
            alt={teamImgNew.alt}
            className={styles.contentTeam}
          ></EntityLogo>
        )}
      </div>

      {isNonNullable(transferState) && isNonNullable(transferState.image) && (
        <div className={styles.secondaryContent}>
          <p
            className={classNames(
              "title-8-regular",
              styles.secondaryContentText,
            )}
          >
            {teamNameOld}
          </p>

          <ImageWithFallback
            data-testid={transferState.image.light}
            width={20}
            height={20}
            src={transferState.image.light}
            darkModeSrc={transferState.image.dark}
            alt={""}
            className={styles.contentTransferIcon}
          ></ImageWithFallback>

          <p
            className={classNames(
              "title-8-regular",
              styles.secondaryContentText,
              styles.secondaryContentTextRight,
            )}
          >
            {teamNameNew}
          </p>
        </div>
      )}

      {isNonNullable(transferState) && isNonNullable(transferState.message) && (
        <p className={classNames("title-8-regular", styles.contentRenewal)}>
          {transferState.message}
        </p>
      )}

      {isNonNullable(transferState) &&
        isNonNullable(transferState.footerText) && (
          <p
            className={classNames(
              "title-8-bold",
              styles.footer,
              transferState.likelihood ===
                TransferLikelihood.TRANSFER_LIKELIHOOD_LOW && styles.footerBlue,
              transferState.likelihood ===
                TransferLikelihood.TRANSFER_LIKELIHOOD_MEDIUM &&
                styles.footerOrange,
              transferState.likelihood ===
                TransferLikelihood.TRANSFER_LIKELIHOOD_HIGH && styles.footerRed,
            )}
          >
            {transferState.footerText}
          </p>
        )}
    </WrapperTag>
  );
};
